import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Truck, Search, Activity, MapPin, User, CreditCard, Tool, Droplet, FileText, DollarSign } from "react-feather";
import DataTable from "../../../../../components/common/DataTable";
import StatusBadge from "../../../../../components/common/StatusBadge";
import DateRangePicker, { useDatePresetRange } from "../../../../../components/common/DateRangePicker";
import { MAROON, MAROON_TINT } from "../../../../../components/common/brandColors";
import ListExportToolbar from "../../../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../../../branchFilter/useBranchFilter";
import { useClaims } from "../../../../auth/useClaims";
import { useGetVehiclesQuery, useListMaintenanceRequestsQuery, useGetMaintenanceRequestQuery } from "../../vehiclesApi";
import { useListLposQuery } from "../../../../stock/stockApi";
import { useListPettyCashVouchersQuery } from "../../../../pettycash/pettyCashApi";
import { useListFuelTransactionsQuery, useListFuelRefillsQuery } from "../../../../fuel/fuelApi";
import { FUEL_REFILL_READ, FUEL_TRANSACTION_READ } from "../../../../fuel/useFuelPermissions";
import VehicleRequestDetailCard from "../../components/VehicleRequestDetailCard";
import { vehicleTypeLabel } from "../../vehicleTypes";
import { paths } from "../../../../../routes/paths";

const money = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-");
// Registrations are written every which way in free text ("KCJ 852X", "kcj852x").
const squash = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const CATEGORY = {
  PETTY_CASH: { label: "Petty Cash", icon: DollarSign, color: "#B02A37", bg: "#FCEBEC" },
  MAINTENANCE: { label: "Maintenance", icon: Tool, color: "#0B5ED7", bg: "#E7F0FD" },
  FUEL: { label: "Fuel", icon: Droplet, color: "#B35C00", bg: "#FFF3E0" },
};
const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "PETTY_CASH", label: "Petty Cash" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "FUEL", label: "Fuel" },
];
// Rows that never cost anything are listed but left out of every total.
const NOT_COUNTED = new Set(["REJECTED", "CANCELLED", "VOIDED"]);

const CategoryBadge = ({ category }) => {
  const meta = CATEGORY[category];
  if (!meta) return category;
  const Icon = meta.icon;
  return (
    <span className="d-inline-flex align-items-center gap-1 rounded px-2 py-1 small fw-semibold" style={{ background: meta.bg, color: meta.color }}>
      <Icon size={13} /> {meta.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, color, label, value, sub }) => (
  <div className="card h-100 mb-0">
    <div className="card-body d-flex align-items-center gap-3 py-3">
      <span className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 46, height: 46, background: `${color}1A`, color }}>
        <Icon size={22} />
      </span>
      <div>
        <div className="text-muted small">{label}</div>
        <div className="fw-bold" style={{ fontSize: 20, color }}>{value}</div>
        {sub && <div className="text-muted small">{sub}</div>}
      </div>
    </div>
  </div>
);

const InfoFact = ({ icon: Icon, label, children }) => (
  <div className="d-flex align-items-center gap-2 px-3">
    <Icon size={22} className="text-muted" />
    <div>
      <div className="text-muted small">{label}</div>
      <div className="fw-semibold">{children}</div>
    </div>
  </div>
);

// Headless loader - spare-part names live only on a request's detail read.
const RequestDetailLoader = ({ recordGuid, onLoaded }) => {
  const { data } = useGetMaintenanceRequestQuery(recordGuid);
  useEffect(() => {
    if (data) onLoaded(recordGuid, data);
  }, [data, recordGuid, onLoaded]);
  return null;
};

const ExpandedRequestRow = ({ recordGuid }) => {
  const { data } = useGetMaintenanceRequestQuery(recordGuid);
  return (
    <div className="p-2">
      <VehicleRequestDetailCard request={data} />
    </div>
  );
};

const lpoTotal = (lpos) =>
  lpos.filter((l) => l.status !== "REJECTED" && l.status !== "CANCELLED").reduce((s, l) => s + Number(l.netAmount || 0), 0);

// One maintenance request as an expense row. Its cost is the LPO value when
// spares were ordered, else the spares' estimate. Cash asked for on the
// request is NOT added here - it is paid through a petty cash voucher, which
// appears under Petty Cash, so adding it would count the money twice.
const requestRow = (r, lposByRequest, details) => {
  const lpos = lposByRequest.get(r.recordGuid) || [];
  const spares = details[r.recordGuid]?.spareItems || [];
  const estimate = spares.reduce((s, i) => s + Number(i.estimatedCost || 0), 0);
  const ordered = lpoTotal(lpos);
  const spareText = spares
    .map((i) => `${i.productName || i.productCode}${i.qty ? ` — qty ${Number(i.qty)}${i.unit ? ` ${i.unit}` : ""}` : ""}`)
    .join(", ");
  return {
    key: `req-${r.recordGuid}`,
    date: r.requestDate,
    category: "MAINTENANCE",
    regNo: r.regNo,
    vehicleId: r.vehicleId,
    description: [r.serviceRepairDescription || r.cashRequestRemarks, spareText].filter(Boolean).join(" · ") || "-",
    note: ordered > 0 ? `LPO ${lpos.map((l) => l.lpoNumber).join(", ")}` : estimate > 0 ? "Spares estimate" : null,
    reference: r.requestNumber,
    amount: ordered > 0 ? ordered : estimate,
    status: r.status,
    requestGuid: r.recordGuid,
    searchText: [
      r.requestNumber, r.serviceRepairDescription, r.cashRequestRemarks, r.remarks, r.spareSupplierName,
      r.approvalComment, r.rejectionReason, r.requestedByName,
      ...lpos.flatMap((l) => [l.lpoNumber, l.purposeDescription, l.supplierName]),
      ...spares.flatMap((s) => [s.productName, s.productCode]),
    ],
  };
};

const voucherRow = (v, vehicle) => ({
  key: `pcv-${v.recordGuid}`,
  date: v.expenseDate || v.createdDate,
  category: "PETTY_CASH",
  regNo: vehicle?.regNo || null,
  vehicleId: vehicle?.vehicleId ?? null,
  description: v.description || "-",
  note: [v.categoryName, v.supplierName].filter(Boolean).join(" · ") || null,
  reference: v.voucherNumber,
  amount: Number(v.paidAmount ?? v.approvedAmount ?? v.requestedAmount ?? 0),
  status: v.status,
  linkTo: paths.pettyCashVoucherDetail(v.recordGuid),
  searchText: [v.voucherNumber, v.description, v.supplierName, v.categoryName, v.requestedByName],
});

const fuelRow = (t) => ({
  key: `ft-${t.recordGuid}`,
  date: t.transactionDate,
  category: "FUEL",
  regNo: t.regNo,
  vehicleId: t.vehicleId,
  description: `Fuel card ${t.cardNumber} (${Number(t.litres)} L × ${Number(t.pricePerLitre)})${t.stationName ? ` @ ${t.stationName}` : ""}`,
  note: t.recordedByName || null,
  reference: t.transactionNumber,
  amount: Number(t.totalAmount || 0),
  status: t.status,
  linkTo: `${paths.fuelTransactions}?open=${t.recordGuid}`,
  searchText: [t.transactionNumber, t.stationName, t.remarks, t.cardNumber],
});

const cashFuelRow = (r) => ({
  key: `fr-${r.recordGuid}`,
  date: r.refillDate,
  category: "FUEL",
  regNo: r.regNo,
  vehicleId: r.vehicleId,
  description: `Direct cash for fuel${r.remarks ? ` — ${r.remarks}` : ""}`,
  note: r.approvedByName ? `Approved by ${r.approvedByName}` : null,
  reference: r.refillNumber,
  amount: Number(r.refillAmount || 0),
  status: r.status,
  linkTo: paths.fuelRefills,
  searchText: [r.refillNumber, r.remarks, r.referenceNumber],
});

const byDateDesc = (a, b) => new Date(b.date) - new Date(a.date);
const counted = (rows) => rows.filter((r) => !NOT_COUNTED.has(r.status));
const sum = (rows) => counted(rows).reduce((s, r) => s + r.amount, 0);

// Vehicle Expense Explorer (2026-10-29, user's mock-up): every rupee spent on
// one vehicle in a period - petty cash, maintenance and fuel - in one list,
// plus a keyword search ("windscreen", "tyre") across every vehicle. Built
// only from existing endpoints; each source is skipped when the caller lacks
// its permission, so a partial picture is labelled rather than a 403.
//
// Petty cash vouchers have no vehicle field: a voucher belongs to a vehicle
// when its description names the registration (the maintenance hand-off tags
// every voucher it raises with "Vehicle: <RegNo>", and staff usually type it).
const VehicleExpenseExplorerPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { permissions } = useClaims();
  const hasAny = (codes) => codes.some((c) => permissions.includes(c));
  const canRequests = permissions.includes("Vehicles.ViewRequests");
  const canLpo = permissions.includes("Stock.ViewLpo");
  const canVouchers = permissions.includes("PettyCash.View");
  const canFuel = hasAny(FUEL_TRANSACTION_READ);
  const canCashFuel = hasAny(FUEL_REFILL_READ);

  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [vehicleId, setVehicleId] = useState(null);
  const [tab, setTab] = useState("ALL");
  const [keywordInput, setKeywordInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [keywordCategory, setKeywordCategory] = useState("");
  const [details, setDetails] = useState({});
  const onDetailLoaded = React.useCallback((guid, data) => setDetails((prev) => ({ ...prev, [guid]: data })), []);

  const branchId = selectedBranchId ?? undefined;
  const range = { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };

  const { data: vehicles = [] } = useGetVehiclesQuery({ branchId });
  const vehicle = vehicles.find((v) => v.vehicleId === vehicleId) || null;
  const vehicleOptions = useMemo(
    () =>
      vehicles.map((v) => ({
        value: v.vehicleId,
        label: `${v.regNo} — ${`${v.brand} ${v.model}`.trim()}`,
        search: `${squash(v.regNo)} ${v.brand} ${v.model} ${v.allocatedToEmployeeName || ""}`.toLowerCase(),
      })),
    [vehicles]
  );
  const regIndex = useMemo(() => vehicles.map((v) => ({ v, key: squash(v.regNo) })).filter((x) => x.key.length >= 5), [vehicles]);
  const vehicleInText = (...texts) => {
    const text = squash(texts.join(" "));
    return regIndex.find((x) => text.includes(x.key))?.v || null;
  };

  const { data: lpoPage } = useListLposQuery({ purposeType: "VEHICLE_MAINTENANCE", branchId, pageSize: 200 }, { skip: !canLpo });
  const lposByRequest = useMemo(() => {
    const map = new Map();
    (lpoPage?.items || []).forEach((l) => {
      if (!l.vehicleRequestRecordGuid) return;
      map.set(l.vehicleRequestRecordGuid, [...(map.get(l.vehicleRequestRecordGuid) || []), l]);
    });
    return map;
  }, [lpoPage]);

  // ---------- one vehicle ----------
  const noVehicle = !vehicleId;
  const { data: reqPage, isFetching: reqLoading } = useListMaintenanceRequestsQuery(
    { branchId, vehicleId: vehicleId ?? undefined, ...range, pageSize: 200 },
    { skip: noVehicle || !canRequests }
  );
  // Two searches because SQL LIKE can't see "KCJ 852 X" in "KCJ852X".
  const regSpaced = vehicle?.regNo?.trim() || "";
  const regTight = regSpaced.replace(/\s+/g, "");
  const { data: pcvA, isFetching: pcvLoadingA } = useListPettyCashVouchersQuery(
    { search: regSpaced, branchId, ...range, pageSize: 200 },
    { skip: noVehicle || !canVouchers || !regSpaced }
  );
  const { data: pcvB, isFetching: pcvLoadingB } = useListPettyCashVouchersQuery(
    { search: regTight, branchId, ...range, pageSize: 200 },
    { skip: noVehicle || !canVouchers || !regTight || regTight === regSpaced }
  );
  const { data: fuelPage, isFetching: fuelLoading } = useListFuelTransactionsQuery(
    { branchId, vehicleId: vehicleId ?? undefined, ...range, pageSize: 2000 },
    { skip: noVehicle || !canFuel }
  );
  const { data: cashPage } = useListFuelRefillsQuery(
    { branchId, status: "APPROVED", paymentMode: "CASH", ...range, pageSize: 1000 },
    { skip: !canCashFuel || (noVehicle && !keyword) }
  );

  const vehicleRequests = useMemo(() => (noVehicle ? [] : reqPage?.items || []), [noVehicle, reqPage]);

  const vehicleRows = useMemo(() => {
    if (!vehicle) return [];
    const key = squash(vehicle.regNo);
    const vouchers = new Map();
    [...(pcvA?.items || []), ...(pcvB?.items || [])].forEach((v) => {
      if (squash(`${v.description} ${v.supplierName || ""}`).includes(key)) vouchers.set(v.recordGuid, v);
    });
    return [
      ...[...vouchers.values()].map((v) => voucherRow(v, vehicle)),
      ...vehicleRequests.map((r) => requestRow(r, lposByRequest, details)),
      ...(fuelPage?.items || []).map(fuelRow),
      ...(cashPage?.items || []).filter((r) => r.vehicleId === vehicle.vehicleId).map(cashFuelRow),
    ].sort(byDateDesc);
  }, [vehicle, pcvA, pcvB, vehicleRequests, lposByRequest, details, fuelPage, cashPage]);

  const pettyRows = vehicleRows.filter((r) => r.category === "PETTY_CASH");
  const maintRows = vehicleRows.filter((r) => r.category === "MAINTENANCE");
  const fuelRows = vehicleRows.filter((r) => r.category === "FUEL");
  const tabRows = { ALL: vehicleRows, PETTY_CASH: pettyRows, MAINTENANCE: maintRows, FUEL: fuelRows }[tab];

  const latestMileage = useMemo(() => {
    const readings = [
      ...vehicleRequests.map((r) => Number(r.currentMileage || 0)),
      ...(fuelPage?.items || []).filter((t) => t.status !== "VOIDED").map((t) => Number(t.odometerReading || 0)),
      Number(vehicle?.lastServiceMileage || 0),
    ];
    const max = Math.max(0, ...readings);
    return max > 0 ? max : null;
  }, [vehicleRequests, fuelPage, vehicle]);
  const latestRequestNumber = vehicleRequests[0]?.requestNumber;

  // ---------- keyword across all vehicles ----------
  const { data: kwReqPage, isFetching: kwReqLoading } = useListMaintenanceRequestsQuery(
    { branchId, ...range, pageSize: 200 },
    { skip: !keyword || !canRequests }
  );
  const { data: kwPcv, isFetching: kwPcvLoading } = useListPettyCashVouchersQuery(
    { search: keyword, branchId, ...range, pageSize: 200 },
    { skip: !keyword || !canVouchers }
  );
  const { data: kwFuel, isFetching: kwFuelLoading } = useListFuelTransactionsQuery(
    { branchId, search: keyword, ...range, pageSize: 1000 },
    { skip: !keyword || !canFuel }
  );
  const keywordRequests = useMemo(() => (keyword ? kwReqPage?.items || [] : []), [keyword, kwReqPage]);

  const keywordRows = useMemo(() => {
    if (!keyword) return [];
    const k = keyword.toLowerCase();
    const hit = (row) => row.searchText.some((t) => t != null && String(t).toLowerCase().includes(k));
    return [
      ...(kwPcv?.items || []).map((v) => voucherRow(v, vehicleInText(v.description, v.supplierName))),
      ...keywordRequests.map((r) => requestRow(r, lposByRequest, details)).filter(hit),
      ...(kwFuel?.items || []).map(fuelRow).filter(hit),
      ...(cashPage?.items || []).map(cashFuelRow).filter(hit),
    ]
      .filter((r) => !keywordCategory || r.category === keywordCategory)
      .sort(byDateDesc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, keywordCategory, kwPcv, keywordRequests, lposByRequest, details, kwFuel, cashPage, regIndex]);

  const detailGuids = useMemo(
    () => [...new Set([...vehicleRequests, ...keywordRequests].map((r) => r.recordGuid))],
    [vehicleRequests, keywordRequests]
  );

  const runKeyword = () => setKeyword(keywordInput.trim());

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const hidden = [
    !canVouchers && "Petty Cash (needs PettyCash.View)",
    !canRequests && "Maintenance (needs Vehicles.ViewRequests)",
    !canFuel && "Fuel (needs a fuel view permission)",
  ].filter(Boolean);

  // The requests/vouchers endpoints serve at most 200 rows per call.
  const truncated = [reqPage, pcvA, pcvB, kwReqPage, kwPcv].some((p) => p && p.totalCount > (p.items || []).length);

  const columns = (withVehicle) => [
    { title: "Date", dataIndex: "date", render: fmtDate, width: 110 },
    withVehicle && {
      title: "Vehicle",
      dataIndex: "regNo",
      render: (v, r) =>
        v ? (
          <button type="button" className="btn btn-link p-0 fw-semibold" onClick={() => r.vehicleId && setVehicleId(r.vehicleId)}>
            {v}
          </button>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    { title: "Category", dataIndex: "category", render: (v) => <CategoryBadge category={v} /> },
    {
      title: "Description / Remarks",
      dataIndex: "description",
      render: (v, r) => (
        <>
          {v}
          {r.note && <div className="text-muted small">{r.note}</div>}
        </>
      ),
    },
    { title: "Ref No.", dataIndex: "reference", render: (v, r) => (r.linkTo ? <Link to={r.linkTo}>{v}</Link> : v) },
    {
      title: "Amount (KSh)",
      dataIndex: "amount",
      align: "right",
      render: (v, r) => (
        <span className={`fw-semibold ${NOT_COUNTED.has(r.status) ? "text-muted text-decoration-line-through" : ""}`}>{money(v)}</span>
      ),
    },
    { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} /> },
  ].filter(Boolean);

  const exportColumns = (withVehicle) => [
    { header: "Date", value: (r) => fmtDate(r.date) },
    ...(withVehicle ? [{ header: "Vehicle", value: (r) => r.regNo || "-" }] : []),
    { header: "Category", value: (r) => CATEGORY[r.category]?.label || r.category },
    { header: "Description", value: (r) => [r.description, r.note].filter(Boolean).join(" · ") },
    { header: "Ref No.", value: (r) => r.reference },
    { header: "Amount", value: (r) => money(r.amount), align: "right" },
    { header: "Status", value: (r) => r.status },
  ];

  const expandable = {
    rowExpandable: (r) => Boolean(r.requestGuid),
    expandedRowRender: (r) => <ExpandedRequestRow recordGuid={r.requestGuid} />,
  };

  const TABS = [
    ["ALL", "All Expenses", vehicleRows.length],
    ["PETTY_CASH", "Petty Cash", pettyRows.length],
    ["MAINTENANCE", "Maintenance", maintRows.length],
    ["FUEL", "Fuel", fuelRows.length],
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {detailGuids.map((guid) => (
          <RequestDetailLoader key={guid} recordGuid={guid} onLoaded={onDetailLoaded} />
        ))}

        <div className="page-header">
          <div className="page-title">
            <h4>Vehicle Expense Explorer</h4>
            <h6>View and search all vehicle related expenses (Fuel, Maintenance, Petty Cash)</h6>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body d-flex flex-wrap gap-3 align-items-end">
            <div style={{ minWidth: 320, flex: "1 1 320px" }}>
              <label className="form-label small fw-semibold mb-1">Select Vehicle</label>
              <Select
                options={vehicleOptions}
                value={vehicleOptions.find((o) => o.value === vehicleId) || null}
                onChange={(o) => setVehicleId(o ? o.value : null)}
                filterOption={(o, input) => {
                  const q = input.trim().toLowerCase();
                  return !q || o.data.search.includes(q) || o.data.search.includes(squash(q).toLowerCase());
                }}
                placeholder="Type reg no., make or driver..."
                isClearable
                menuPortalTarget={document.body}
                styles={{ menuPortal: (b) => ({ ...b, zIndex: 2000 }), control: (b) => ({ ...b, minHeight: 38 }) }}
              />
            </div>
            <div>
              <label className="form-label small fw-semibold mb-1 d-block">Date Range</label>
              <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
            </div>
          </div>
        </div>

        {vehicle ? (
          <>
            <div className="card mb-3">
              <div className="card-body d-flex flex-wrap align-items-center gap-3">
                <div className="d-flex align-items-center gap-3 pe-3 border-end">
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 64, height: 64, background: MAROON_TINT, color: MAROON }}>
                    <Truck size={30} />
                  </span>
                  <div>
                    <div className="fw-bold" style={{ fontSize: 20 }}>{vehicle.regNo}</div>
                    <div className="text-muted">{`${vehicle.brand} ${vehicle.model}`.trim()}</div>
                    <div className="text-muted small">
                      {vehicleTypeLabel(vehicle.vehicleType)}
                      {latestRequestNumber ? ` · ${latestRequestNumber}` : ""}
                    </div>
                  </div>
                </div>
                <InfoFact icon={Activity} label="Latest Mileage">
                  {latestMileage ? `${latestMileage.toLocaleString()} km` : "-"}
                </InfoFact>
                <InfoFact icon={MapPin} label="Branch">{vehicle.branchCode}</InfoFact>
                <InfoFact icon={User} label="Driver">
                  <span className="text-primary">{vehicle.allocatedToEmployeeName || "-"}</span>
                </InfoFact>
                <div className="ms-auto">
                  <span className={`badge ${vehicle.isActive ? "badge-linesuccess" : "badge-linedanger"}`}>
                    {vehicle.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-12 col-md">
                <StatCard icon={CreditCard} color={MAROON} label="Total Expenses" value={`KSh ${money(sum(vehicleRows))}`} sub="All categories" />
              </div>
              <div className="col-6 col-md">
                <StatCard icon={DollarSign} color={CATEGORY.PETTY_CASH.color} label="Petty Cash" value={`KSh ${money(sum(pettyRows))}`} sub="Vouchers naming this vehicle" />
              </div>
              <div className="col-6 col-md">
                <StatCard icon={Tool} color={CATEGORY.MAINTENANCE.color} label="Maintenance" value={`KSh ${money(sum(maintRows))}`} sub="LPO value / spares estimate" />
              </div>
              <div className="col-6 col-md">
                <StatCard icon={Droplet} color={CATEGORY.FUEL.color} label="Fuel" value={`KSh ${money(sum(fuelRows))}`} sub="Card & cash fuel" />
              </div>
              <div className="col-6 col-md">
                <StatCard icon={FileText} color="#6F42C1" label="Transactions" value={vehicleRows.length} sub="Total records" />
              </div>
            </div>
          </>
        ) : (
          <div className="alert alert-light border mb-3">
            Pick a vehicle above to see all its expenses, or search a keyword below across every vehicle.
          </div>
        )}

        <div className="card mb-3" style={{ background: MAROON_TINT, borderColor: `${MAROON}33` }}>
          <div className="card-body">
            <h5 className="mb-2" style={{ color: MAROON }}>Search expenses across ALL vehicles</h5>
            <div className="d-flex flex-wrap gap-2">
              <div className="input-group" style={{ flex: "1 1 320px" }}>
                <span className="input-group-text bg-white"><Search size={16} /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. windscreen, brake lining, tyre, service"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runKeyword()}
                />
              </div>
              <select className="form-control" style={{ width: 190 }} value={keywordCategory} onChange={(e) => setKeywordCategory(e.target.value)}>
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button type="button" className="btn text-white d-inline-flex align-items-center gap-1" style={{ background: MAROON }} onClick={runKeyword}>
                <Search size={16} /> Search
              </button>
              {keyword && (
                <button type="button" className="btn btn-outline-secondary" onClick={() => { setKeyword(""); setKeywordInput(""); }}>
                  Clear
                </button>
              )}
            </div>
            <div className="text-muted small mt-1">
              Searches descriptions, remarks, suppliers and spare-part names across every vehicle, in the selected date range.
            </div>
          </div>
        </div>

        {keyword && (
          <div className="card table-list-card mb-3">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0 d-inline-flex align-items-center gap-2" style={{ color: MAROON }}>
                  <Search size={18} /> Keyword results: &lsquo;{keyword}&rsquo;
                </h5>
                <div className="d-flex align-items-center gap-3">
                  <span className="small text-muted">
                    {keywordRows.length} record{keywordRows.length === 1 ? "" : "s"} · total{" "}
                    <strong className="tx-money tx-money-out">KSh {money(sum(keywordRows))}</strong> ·{" "}
                    {new Set(keywordRows.filter((r) => r.regNo).map((r) => r.regNo)).size} vehicles
                  </span>
                  <ListExportToolbar
                    title={`Vehicle expenses - '${keyword}'`}
                    subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                    branchCode={branchCode}
                    columns={exportColumns(true)}
                    rows={keywordRows}
                    fileName="VehicleExpenseKeyword"
                  />
                </div>
              </div>
              <DataTable
                columns={columns(true)}
                dataSource={keywordRows}
                rowKey="key"
                loading={kwReqLoading || kwPcvLoading || kwFuelLoading}
                expandable={expandable}
              />
              <div className="text-muted small mt-1">Vehicle on a petty cash voucher is read from the registration written in its description; &ldquo;-&rdquo; = none written.</div>
            </div>
          </div>
        )}

        {vehicle && (
          <div className="card table-list-card">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <ul className="nav nav-tabs border-0">
                  {TABS.map(([key, label, count]) => (
                    <li className="nav-item" key={key}>
                      <button
                        type="button"
                        className={`nav-link border-0 ${tab === key ? "fw-bold" : "text-muted"}`}
                        style={tab === key ? { color: MAROON, borderBottom: `3px solid ${MAROON}` } : undefined}
                        onClick={() => setTab(key)}
                      >
                        {label} ({count})
                      </button>
                    </li>
                  ))}
                </ul>
                <ListExportToolbar
                  title={`Vehicle expenses - ${vehicle.regNo}`}
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns(false)}
                  rows={tabRows}
                  fileName={`VehicleExpenses_${squash(vehicle.regNo)}`}
                />
              </div>
              <DataTable
                columns={columns(false)}
                dataSource={tabRows}
                rowKey="key"
                loading={reqLoading || pcvLoadingA || pcvLoadingB || fuelLoading}
                expandable={expandable}
              />
              <div className="text-muted small mt-2">
                Maintenance cost = linked LPO value, else the spares estimate. Cash requested on a maintenance request is paid by
                petty cash voucher and counted under Petty Cash only. Rejected, cancelled and voided rows are struck through and
                left out of totals.
              </div>
            </div>
          </div>
        )}

        {truncated && (
          <div className="alert alert-warning small mt-2">
            Some lists hit the 200-record limit - narrow the date range for a complete picture.
          </div>
        )}

        {hidden.length > 0 && (
          <div className="alert alert-light border small mt-2">Not shown for your role: {hidden.join(", ")}.</div>
        )}
      </div>
    </div>
  );
};

export default VehicleExpenseExplorerPage;
